import { RouteProp } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { AHTeamIcon, AHText, Box, Card, Circle, ListItem } from "arkh";
import React, { useCallback, useEffect, useMemo } from "react";
import { ListRenderItem } from "react-native";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";

import { SelectList } from "arkh/src/cross-platform/SelectList/SelectList";
import { EmployeeScheduleStackParamList } from "./EmployeeScheduleEditStack";
import { EmployeeScheduleEditScheduleTypesAssignTeam$key } from "./__generated__/EmployeeScheduleEditScheduleTypesAssignTeam.graphql";
import { EmployeeScheduleEditScheduleTypesAssignTeamMutation } from "./__generated__/EmployeeScheduleEditScheduleTypesAssignTeamMutation.graphql";
import { EmployeeScheduleEditScheduleTypesAssignTeamQuery } from "./__generated__/EmployeeScheduleEditScheduleTypesAssignTeamQuery.graphql";
import { EmployeeScheduleEditScheduleTypesAssignTeam_scheduleType$key } from "./__generated__/EmployeeScheduleEditScheduleTypesAssignTeam_scheduleType.graphql";
import { useShowCancelWarningAlert } from "../../common/useCancelWarning";
import { useModalInsets } from "../../components/mobile/useModalInsets";
import { FormattedMessage } from "react-intl";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";

export function EmployeeScheduleEditScheduleTypesAssignTeamScreen(props: {
  route: RouteProp<
    EmployeeScheduleStackParamList,
    "EmployeeScheduleEditScheduleTypesAssignTeam"
  >;
  navigation: NativeStackNavigationProp<
    EmployeeScheduleStackParamList,
    "EmployeeScheduleEditScheduleTypesAssignTeam"
  >;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <EmployeeScheduleEditScheduleTypesAssignTeamComponent
          {...props}
          fk={fk}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function EmployeeScheduleEditScheduleTypesAssignTeamComponent(props: {
  route: RouteProp<
    EmployeeScheduleStackParamList,
    "EmployeeScheduleEditScheduleTypesAssignTeam"
  >;
  navigation: NativeStackNavigationProp<
    EmployeeScheduleStackParamList,
    "EmployeeScheduleEditScheduleTypesAssignTeam"
  >;
  fk: string;
}) {
  const [mutate, isSaving] =
    useMutation<EmployeeScheduleEditScheduleTypesAssignTeamMutation>(graphql`
      mutation EmployeeScheduleEditScheduleTypesAssignTeamMutation(
        $teamId: ID!
        $scheduleTypeIds: [ID!]!
      ) {
        team(id: $teamId) {
          setAssignedEmployeeScheduleTypes(scheduleTypeIds: $scheduleTypeIds) {
            id
            store {
              ...EmployeeScheduleEditScheduleTypesAssignTeam
              employeeScheduleTypes {
                id
                assignedTeams {
                  id
                }
              }
            }
          }
        }
      }
    `);
  const data =
    useLazyLoadQuery<EmployeeScheduleEditScheduleTypesAssignTeamQuery>(
      graphql`
        query EmployeeScheduleEditScheduleTypesAssignTeamQuery {
          store {
            ...EmployeeScheduleEditScheduleTypesAssignTeam
            employeeScheduleTypes {
              id
              assignedTeams {
                id
              }
            }
          }
        }
      `,
      {},
      { fetchKey: props.fk, fetchPolicy: "store-and-network" }
    );
  const remoteTypeIds = useMemo(
    () =>
      data.store.employeeScheduleTypes
        .filter((type) =>
          type.assignedTeams.some(
            (team) => team.id === props.route.params.teamId
          )
        )
        .map((type) => type.id),

    [data.store.employeeScheduleTypes, props.route.params.teamId]
  );

  const [localTeams, setLocalTeams] = React.useState<string[] | null>(null);
  useEffect(() => {
    setLocalTeams(null);
  }, [remoteTypeIds]);

  const selectedTeamIds = useMemo(() => {
    if (localTeams) {
      return localTeams;
    }
    return remoteTypeIds;
  }, [localTeams, remoteTypeIds]);

  const save = useCallback(() => {
    mutate({
      variables: {
        teamId: props.route.params.teamId,
        scheduleTypeIds: selectedTeamIds,
      },
      onCompleted: () => {
        props.navigation.goBack();
        setLocalTeams(null);
      },
    });
  }, [mutate, props.navigation, props.route.params.teamId, selectedTeamIds]);
  const showCancelWarningAlert = useShowCancelWarningAlert();

  const intl_tokens = useIntlTokens();
  useNavigationHeader({
    type: "SAVE",
    onSave: save,
    defaultContentStyle: true,
    onCancel: () => {
      if (localTeams === null) {
        props.navigation.goBack();
      } else {
        showCancelWarningAlert(props.navigation.goBack);
      }
    },
    saveLoading: isSaving,
    disabled: localTeams === null,
    title: props.route.params.teamName || intl_tokens.AssignScheduleTypes,
  });

  return (
    <List
      store={data.store}
      selectedTeamIds={selectedTeamIds}
      teamId={props.route.params.teamId}
      onPressItem={(id: string) => {
        if (selectedTeamIds.includes(id)) {
          setLocalTeams(selectedTeamIds.filter((teamId) => teamId !== id));
        } else {
          setLocalTeams([...selectedTeamIds, id]);
        }
      }}
    />
  );
}

function List(props: {
  store: EmployeeScheduleEditScheduleTypesAssignTeam$key;
  teamId: string;
  selectedTeamIds: string[];
  onPressItem: (id: string) => void;
}) {
  const store = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleTypesAssignTeam on Store {
        employeeScheduleTypes {
          id

          ...EmployeeScheduleEditScheduleTypesAssignTeam_scheduleType
        }
      }
    `,
    props.store
  );

  const renderScheduleType: ListRenderItem<
    (typeof store.employeeScheduleTypes)[number]
  > = ({ item, index }) => {
    return (
      <ScheduleTypeListItem
        selected={props.selectedTeamIds.includes(item.id)}
        onPress={() => props.onPressItem(item.id)}
        scheduleType={item}
        isFirst={index === 0}
        isLast={index + 1 === store.employeeScheduleTypes.length}
        teamId={props.teamId}
      />
    );
  };
  const insets = useModalInsets();
  return (
    <SelectList
      insets={insets}
      ListHeaderComponent={<ListHeader />}
      renderItem={renderScheduleType}
      data={store.employeeScheduleTypes}
      keyExtractor={(item) => item.id}
    />
  );
}

function ListHeader() {
  return (
    <Box spaceTop="large">
      <Card defaultBorder>
        <AHText style={{ lineHeight: 17 }} type="body">
          <FormattedMessage defaultMessage="Assign cost centers to teams. The schedule from the scheduling tool will be automatically synced. The same cost center can belong to several teams." />
        </AHText>
      </Card>
    </Box>
  );
}
function ScheduleTypeListItem(props: {
  scheduleType: EmployeeScheduleEditScheduleTypesAssignTeam_scheduleType$key;
  isFirst: boolean;
  isLast: boolean;
  selected: boolean;
  onPress: () => void;
  teamId: string;
}) {
  const scheduleType = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleTypesAssignTeam_scheduleType on EmployeeScheduleType {
        id
        title
        color
        assignedTeams {
          id
          color
          name
          icon
        }
      }
    `,
    props.scheduleType
  );

  const assignedTeams = scheduleType.assignedTeams.filter(
    (t) => t.id !== props.teamId
  );
  return (
    <ListItem
      isFirst={props.isFirst}
      isLast={props.isLast}
      onPress={props.onPress}
      border="border-primary"
      left={
        <Circle
          size="small"
          style={{ backgroundColor: scheduleType.color ?? "red" }}
        />
      }
      right={<ListItem.SelectIndicator selected={props.selected} />}
    >
      <ListItem.Title>{scheduleType.title}</ListItem.Title>
      {assignedTeams.length > 0 ? (
        <Box style={{ flex: 1, flexDirection: "row", flexWrap: "wrap" }}>
          {assignedTeams.map((t) => (
            <Box
              style={{ flexDirection: "row", alignItems: "center" }}
              spaceRight="small"
              key={t.id}
            >
              <AHTeamIcon
                size="xxsmall"
                type={t.icon ?? "default"}
                customColor={t.color ?? undefined}
              />
              <AHText spaceLeft="xxsmall" type="detail">
                {t.name}
              </AHText>
            </Box>
          ))}
        </Box>
      ) : null}
    </ListItem>
  );
}
